import {
   Component,
   EventEmitter,
   Input,
   OnChanges,
   Output,
   SimpleChanges
} from '@angular/core';
import {
   FormBuilder,
   FormGroup,
   ReactiveFormsModule,
   Validators
} from '@angular/forms';
import { BillingContact } from '../../../services/response/xero/billing-contact';
import { UpdateBillingContactRequest } from '../../../services/request/xero/update-billing-contact-request';

import { CommonModule } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { PanelModule } from 'primeng/panel';
import { ButtonModule } from 'primeng/button';
import { RadioButtonModule } from 'primeng/radiobutton'; // not used but harmless if already
import { TooltipModule } from 'primeng/tooltip';

@Component({
   selector: 'app-billing-details',
   standalone: true,
   templateUrl: './billing-details.component.html',
   styleUrls: ['./billing-details.component.scss'],
   imports: [
      CommonModule,
      ReactiveFormsModule,
      InputTextModule,
      CheckboxModule,
      PanelModule,
      ButtonModule,
      TooltipModule,
      RadioButtonModule
   ]
})
export class BillingDetailsComponent implements OnChanges {
   @Input() contact: BillingContact | null = null;
   @Input() saving = false;
   @Output() save = new EventEmitter<UpdateBillingContactRequest>();

   billingForm: FormGroup;

   constructor(private fb: FormBuilder) {
      this.billingForm = this.fb.group({
         // Business info
         name: [{ value: '', disabled: true }],

         // Primary contact
         firstName: ['', Validators.required],
         lastName: ['', Validators.required],
         emailAddress: ['', [Validators.required, Validators.email]],

         // Secondary contact
         addSecondaryContact: [false],
         secondaryFirstName: [''],
         secondaryLastName: [''],
         secondaryEmailAddress: ['', Validators.email],
         secondaryIncludeInEmails: [true],

         // Billing address (POBOX)
         addressLine1: ['', Validators.required],
         addressLine2: [''],
         city: ['', Validators.required],
         region: [''],
         postalCode: ['', Validators.required],
         country: ['', Validators.required],

         // Tax
         taxNumber: ['']
      });
   }

   ngOnChanges(changes: SimpleChanges): void {
      if (changes['contact'] && this.contact) {
         this.patchFromContact(this.contact);
      }
   }

   private patchFromContact(c: BillingContact) {
      // Primary contact
      const primaryFirstName = c.FirstName || '';
      const primaryLastName = c.LastName || '';
      const primaryEmail = c.EmailAddress || '';

      // Secondary contact (first ContactPersons entry)
      const person = c.ContactPersons && c.ContactPersons[0];
      const hasSecondary =
         !!person &&
         (!!person.EmailAddress ||
            !!person.FirstName ||
            !!person.LastName ||
            person.IncludeInEmails !== undefined);

      // POBOX address
      const poBox = c.Addresses?.find((a) => a.AddressType === 'POBOX') ?? {};

      this.billingForm.patchValue(
         {
            name: c.Name || '',

            firstName: primaryFirstName,
            lastName: primaryLastName,
            emailAddress: primaryEmail,

            addSecondaryContact: hasSecondary,
            secondaryFirstName: person?.FirstName || '',
            secondaryLastName: person?.LastName || '',
            secondaryEmailAddress: person?.EmailAddress || '',
            secondaryIncludeInEmails:
               person?.IncludeInEmails !== undefined
                  ? !!person.IncludeInEmails
                  : true,

            addressLine1: poBox.AddressLine1 || '',
            addressLine2: poBox.AddressLine2 || '',
            city: poBox.City || '',
            region: poBox.Region || '',
            postalCode: poBox.PostalCode || '',
            country: poBox.Country || '',

            taxNumber: c.TaxNumber || ''
         },
         { emitEvent: false }
      );

      this.billingForm.markAsPristine();
   }

   isInvalid(controlName: string): boolean {
      const control = this.billingForm.get(controlName);
      return !!(control && control.invalid && (control.touched || control.dirty));
   }

   onToggleSecondaryContact(checked: boolean) {
      if (!checked) {
         this.billingForm.patchValue(
            {
               secondaryFirstName: '',
               secondaryLastName: '',
               secondaryEmailAddress: '',
               secondaryIncludeInEmails: true
            },
            { emitEvent: false }
         );
      }
   }

   onSubmit() {
      if (this.billingForm.invalid) {
         this.billingForm.markAllAsTouched();
         return;
      }

      const raw = this.billingForm.getRawValue(); // includes disabled 'name'

      const payload: UpdateBillingContactRequest = {
         name: raw.name || undefined,
         firstName: raw.firstName || undefined,
         lastName: raw.lastName || undefined,
         emailAddress: raw.emailAddress || undefined,
         taxNumber: raw.taxNumber || undefined
      };

      // Secondary contact → contactPersons
      if (raw.addSecondaryContact && raw.secondaryEmailAddress) {
         payload.contactPersons = [
            {
               firstName: raw.secondaryFirstName || undefined,
               lastName: raw.secondaryLastName || undefined,
               emailAddress: raw.secondaryEmailAddress || undefined,
               includeInEmails: !!raw.secondaryIncludeInEmails
            }
         ];
      } else {
         // Explicitly clear contact persons when unchecked / empty
         payload.contactPersons = [];
      }

      // POBOX address
      if (
         raw.addressLine1 ||
         raw.addressLine2 ||
         raw.city ||
         raw.region ||
         raw.postalCode ||
         raw.country
      ) {
         payload.poBoxAddress = {
            addressLine1: raw.addressLine1 || undefined,
            addressLine2: raw.addressLine2 || undefined,
            city: raw.city || undefined,
            region: raw.region || undefined,
            postalCode: raw.postalCode || undefined,
            country: raw.country || undefined
         };
      }

      this.save.emit(payload);
   }
}
